<template>
    <div
        :class="[
            'ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-2xl ppv2-p-6 ppv2-flex ppv2-flex-col ppv2-gap-4 ppv2-transition-all ppv2-duration-150',
            disabled
                ? 'ppv2-cursor-not-allowed ppv2-opacity-60'
                : 'ppv2-cursor-pointer hover:ppv2-translate-y-[-2px] hover:ppv2-shadow-md',
        ]"
        @click="handleClick"
    >
        <!-- Icon Container -->
        <span
            class="ppv2-w-12 ppv2-h-12 ppv2-rounded-[13px] ppv2-flex ppv2-items-center ppv2-justify-center bg-purple ppv2-bg-opacity-10 ppv2-text-[#7d2dbd]"
        >
            <slot name="icon">
                <!-- Fallback to SVG based on card id -->
                <svg
                    v-if="id === 1"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <rect x="4" y="3" width="16" height="18" rx="1.5"></rect>
                    <path d="M9 7h.01"></path>
                    <path d="M15 7h.01"></path>
                    <path d="M9 11h.01"></path>
                    <path d="M15 11h.01"></path>
                    <path d="M10 21v-3.5h4V21"></path>
                </svg>
                <svg
                    v-else-if="id === 2"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <circle cx="12" cy="12" r="9"></circle>
                    <path d="M12 7.5V12l3 2"></path>
                </svg>
                <svg
                    v-else-if="id === 3"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <rect x="2.5" y="5" width="19" height="14" rx="2.5"></rect>
                    <path d="M2.5 9.5h19"></path>
                </svg>
                <svg
                    v-else-if="id === 4"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path d="M12 3l7.5 2.8v5.2c0 4.6-3.2 7.4-7.5 8.5-4.3-1.1-7.5-3.9-7.5-8.5V5.8L12 3z"></path>
                    <path d="M9 12l2 2 4-4"></path>
                </svg>
                <svg
                    v-else-if="id === 5"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <rect x="3" y="4.5" width="18" height="16.5" rx="2"></rect>
                    <path d="M3 9.5h18"></path>
                    <path d="M8 2.5v4"></path>
                    <path d="M16 2.5v4"></path>
                    <path d="M9 15l2 2 4-4"></path>
                </svg>
                <img v-else-if="iconSrc" :src="iconSrc" :alt="title" class="ppv2-w-6 ppv2-h-6" />
            </slot>
        </span>

        <!-- Card Content -->
        <div class="ppv2-flex ppv2-flex-col ppv2-gap-1">
            <div class="poppinsSemiBold ppv2-text-[16.5px] ppv2-text-black ppv2-leading-tight">
                {{ title }}
            </div>
            <div class="poppinsRegular ppv2-text-[13px] ppv2-text-black ppv2-text-opacity-80 ppv2-leading-normal ppv2-mt-1">
                {{ description }}
            </div>
        </div>

        <!-- Button -->
        <button
            :disabled="disabled"
            @click.stop="handleClick"
            :class="[
                'ppv2-mt-auto ppv2-w-full ppv2-flex ppv2-items-center ppv2-justify-between ppv2-py-2.5 ppv2-px-4 ppv2-rounded-lg ppv2-border ppv2-border-solid ppv2-border-opacity-20 poppinsSemiBold ppv2-text-sm ppv2-transition-all ppv2-duration-150',
                disabled ? 'ppv2-bg-black ppv2-bg-opacity-[7%] ppv2-cursor-not-allowed' : 'ppv2-text-black ppv2-cursor-pointer',
            ]"
        >
            {{ buttonText }}
            <span class="ppv2-flex">
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path d="M9 5l7 7-7 7"></path>
                </svg>
            </span>
        </button>
    </div>
</template>

<script>
export default {
    name: 'ConfigurationCard',
    props: {
        id: {
            type: [Number, String],
            default: null,
        },
        title: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            required: true,
        },
        status: {
            type: String,
            required: true,
            validator: (value) => ['completed', 'in-progress', 'incomplete'].includes(value),
        },
        percentage: {
            type: Number,
            required: true,
            validator: (value) => value >= 0 && value <= 100,
        },
        buttonText: {
            type: String,
            required: true,
        },
        buttonStyle: {
            type: String,
            default: 'secondary',
            validator: (value) => ['primary', 'secondary'].includes(value),
        },
        iconSrc: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        handleClick() {
            if (!this.disabled) {
                this.$emit('card-action')
            }
        },
    },
}
</script>

<style scoped>
/* No additional scoped styles needed as we use Tailwind utility classes */
</style>
